import React from 'react';
import AvItemWithLogoGroup, { IconSize } from '@components/AvItemWithLogoGroup';
import { IconVariant } from '@components/ItemWithLogo';
import { NoDataFoundWidget } from '@components/Widgets/layout.components';
import { AvButton, formatDate } from '@fabric-ui/components';
import { Internal } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, Skeleton, Typography } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from '@utils/AvRouter';
import { Entities } from '@views/Threat/Entities';
import useQueryObject from '../../../../hooks/useQueryObjectSql';
import { handleNavigation } from '../../utils';
import { getTop5HighRiskIncidents } from '../queries';
import { FilterContext, TableDataType } from '../types';

interface TopHighRiskIncidentsProps {
  filterContext: FilterContext;
}

interface HighRiskIncident {
  max_incident_risk_mass: number;
  key: string;
  title: string;
  vendors: string[];
  assets: string[];
  users: string[];
  sla: string;
}

const TopHighRiskIncidents = (props: TopHighRiskIncidentsProps) => {
  const res = useQueryObject({
    queryObject: getTop5HighRiskIncidents(props.filterContext),
    options: { placeholderData: keepPreviousData },
  });
  const navigate = useNavigate();
  const data = res.data as HighRiskIncident[];

  return (
    <Box sx={{ ...flex.col, background: 'none', height: '100%' }}>
      <Box sx={{ ...flex.justifyBetween }}>
        <Typography sx={{ whiteSpace: 'nowrap', fontSize: '18px' }} variant="h2">
          Top 5 High Risk Incidents
        </Typography>
        <AvButton
          sx={{ gap: '4px' }}
          size="small"
          variant="primaryText"
          onClick={() => handleNavigation(props.filterContext, TableDataType.INCIDENT, navigate)}>
          Explore Incidents
          <Internal />
        </AvButton>
      </Box>
      {!res.isLoading ? (
        data === undefined || data.length === 0 ? (
          <NoDataFoundWidget />
        ) : (
          <>
            <Box
              sx={{
                ...flex.row,
                gap: '10px',
                marginTop: '16px',
                padding: '5px 0 5px 16px',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.4,
                color: 'var(--color-content-base-secondary)',
              }}>
              <Box sx={{ flex: 1 }}>RISK MASS</Box>
              <Box sx={{ flex: 3 }}>INCIDENTS</Box>
              <Box sx={{ flex: 1 }}>SOURCE</Box>
              <Box sx={{ flex: 2 }}>SLA</Box>
            </Box>

            {data.map(item => (
              <Box
                key={item.key}
                sx={{
                  ...flex.row,
                  padding: '12px 16px',
                  marginTop: '8px',
                  background: `var(--color-surface-base-primary)`,
                }}>
                <Box
                  sx={{
                    flex: 1,
                    fontWeight: 600,
                    fontSize: 18,
                    color: 'var(--color-content-base-primary)',
                  }}>
                  {item.max_incident_risk_mass}
                </Box>
                <Box
                  sx={{
                    flex: 3,
                    padding: '0 16px',
                    borderLeft: '1px solid var(--color-border-base-subdued)',
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--color-content-base-primary)',
                  }}>
                  <div>{item.title}</div>
                  <Entities users={item.users?.length || 0} assets={item.assets?.length || 0} />
                </Box>
                <Box
                  sx={{
                    ...flex.itemsCenter,
                    flex: 1,
                    paddingLeft: '16px',
                    borderLeft: '1px solid var(--color-border-base-subdued)',
                  }}>
                  <Box sx={{ width: '100%' }}>
                    <AvItemWithLogoGroup
                      variant={IconVariant.sourcesMapByName}
                      items={item.vendors || []}
                      size={IconSize.collapsed}
                      limit={1}
                      inputVariantSpace={8}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    flex: 2,
                    paddingLeft: '16px',
                    borderLeft: '1px solid var(--color-border-base-subdued)',
                    fontSize: 14,
                    color: 'var(--color-content-base-primary)',
                  }}>
                  {formatDate(new Date(item.sla), 'MMM dd, yyyy')}
                </Box>
              </Box>
            ))}
          </>
        )
      ) : (
        <Skeleton variant="rectangular" width="100%" height="100%" />
      )}
    </Box>
  );
};

export default TopHighRiskIncidents;
